import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import QRCode from 'qrcode';
import { Layout } from '../../components/Layout.js';
import { API_BASE_URL, api } from '../../lib/api.js';

interface CodeResponse {
  status: 'issued' | 'validated' | 'voided' | 'refunded';
  payload?: string;
  ticket?: { name: string; kind: string };
}

interface WalletStatusResponse {
  google: boolean;
  apple: boolean;
}

interface GoogleWalletResponse {
  saveUrl: string;
}

export function TicketViewerPage() {
  const { qrId } = useParams();
  const { t } = useTranslation('event');
  const [code, setCode] = useState<CodeResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [walletStatus, setWalletStatus] = useState<WalletStatusResponse>({
    google: false,
    apple: false,
  });
  const [googleWalletPending, setGoogleWalletPending] = useState(false);

  // El QR es estático: lo pedimos una sola vez y nunca refrescamos. El
  // mismo string vive en Apple Wallet, Google Wallet y aquí.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<CodeResponse>(`/qr/${qrId}/code`);
        if (cancelled) return;
        setCode(res);
        if (res.status === 'issued' && res.payload) {
          const url = await QRCode.toDataURL(res.payload, {
            margin: 1,
            width: 1024,
            color: { dark: '#0A0A0A', light: '#FFFFFF' },
          });
          if (!cancelled) setQrDataUrl(url);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'unknown');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qrId]);

  // Probe único al status del wallet — más liviano que pegarle al
  // endpoint pesado de cada provider para "detectar si está vivo".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<WalletStatusResponse>('/wallet/status');
        if (!cancelled) setWalletStatus(res);
      } catch {
        // Sin status: dejamos ambos en false (sin botones).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAddToGoogleWallet(): Promise<void> {
    setGoogleWalletPending(true);
    try {
      const res = await api<GoogleWalletResponse>(`/qr/${qrId}/google-wallet`);
      window.open(res.saveUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // Silencioso: el usuario sigue teniendo la PWA como respaldo.
    } finally {
      setGoogleWalletPending(false);
    }
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-24 text-center text-red-400">{error}</div>
      </Layout>
    );
  }

  if (!code) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-24 text-center text-neutral-400">Loading…</div>
      </Layout>
    );
  }

  if (code.status !== 'issued') {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl font-bold mb-2">{t('ticket.voided')}</h1>
        </div>
      </Layout>
    );
  }

  // En iOS Safari, navegar directo al .pkpass abre el instalador del
  // pase nativo. En desktop el browser baja el archivo (a veces lo abre
  // con Wallet en macOS). Usamos `<a>` para que el browser maneje la
  // detección del MIME type sin intervención de JS.
  const appleWalletHref = qrId ? `${API_BASE_URL}/qr/${qrId}/apple-wallet` : undefined;
  const hasAnyWalletButton = walletStatus.google || (walletStatus.apple && appleWalletHref);

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-1">{t('ticket.your_ticket')}</h1>
        <p className="text-neutral-400 mb-8">{code.ticket?.name}</p>
        {qrDataUrl && (
          <img
            src={qrDataUrl}
            alt="QR"
            className="w-full max-w-sm mx-auto rounded-2xl bg-white p-6"
          />
        )}
        <p className="mt-6 text-sm text-neutral-400">{t('ticket.static_qr_help')}</p>
        {hasAnyWalletButton && (
          <div className="mt-8 flex flex-col items-center gap-3">
            {walletStatus.apple && appleWalletHref && (
              <a href={appleWalletHref} className="btn-secondary text-xs">
                {t('ticket.add_to_apple_wallet')}
              </a>
            )}
            {walletStatus.google && (
              <button
                type="button"
                onClick={handleAddToGoogleWallet}
                disabled={googleWalletPending}
                className="btn-secondary text-xs disabled:opacity-50"
              >
                {googleWalletPending
                  ? t('ticket.add_to_google_wallet_pending')
                  : t('ticket.add_to_google_wallet')}
              </button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
